import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DEFAULT_OFFERS_MODE, isOffersMode, type OffersMode } from '@/entities/order';

interface OffersModeState {
  mode: OffersMode;
  setMode: (mode: OffersMode) => void;
}

/** The mode out of whatever a previous build left in storage, else the default. */
function readStoredMode(persisted: unknown): OffersMode {
  const mode =
    typeof persisted === 'object' && persisted !== null && 'mode' in persisted
      ? persisted.mode
      : null;
  return isOffersMode(mode) ? mode : DEFAULT_OFFERS_MODE;
}

/**
 * Which offers source the operator last chose. A durable per-device
 * preference (not server state), so it survives reloads and applies to every
 * order; first-time operators get the legacy behaviour.
 */
export const useOffersModeStore = create<OffersModeState>()(
  persist(
    (set) => ({
      mode: DEFAULT_OFFERS_MODE,
      setMode: (mode) => set({ mode }),
    }),
    {
      name: 'order-offers-mode',
      version: 1,
      partialize: (state) => ({ mode: state.mode }),
      // Never trust storage: an unknown value (edited, or from a future build)
      // falls back to the default instead of selecting a mode that doesn't exist.
      merge: (persisted, current) => ({ ...current, mode: readStoredMode(persisted) }),
    },
  ),
);
